/**
 * useRasterViewport — shared PDF raster + pan/zoom viewport hook (Submittal Review Tool, Phase 3).
 *
 * Extracts the render-core pattern proven in QcMarkupModal.jsx / TakeoffEditor.jsx into a
 * reusable hook: PDF.js rasters a page ONCE to an offscreen canvas at a fixed base scale, and
 * all pan/zoom afterward is a single {x,y,z} transform drawn onto one fixed-size composite
 * canvas — never a re-render of the PDF itself except for the "resharpen" pass that re-rasters
 * at the current zoom level ~220ms after the user stops interacting (so panning/zooming stays
 * smooth, and the view sharpens up once it settles).
 *
 * Coordinate model — deliberately NOT the QC tool's model:
 * QcMarkupModal stores annotations in absolute PDF user-space plus a `page_frame` fingerprint
 * and reprojects on mismatch (see docs/QC_WORKFLOW.md — "the QC coordinate drift bug"). This
 * hook follows the takeoffs model instead (lib/pdfFrame.js, TakeoffEditor.jsx): screenToFrac /
 * fracToScreen convert between screen pixels and PAGE-RELATIVE VISUAL FRACTIONS [fx, fy] in the
 * 0..1 range, derived from the base raster canvas's own pixel dimensions (which pdf.js already
 * rotation-normalizes via getViewport). Fractions are frame-independent by construction — a
 * consumer that persists shapes in fractions never needs to reproject them. The pure transform
 * math (`screenToFracPure` / `fracToScreenPure`) is exported standalone so it can be unit-tested
 * without React — see useRasterViewport.test.mjs.
 *
 * This hook is intentionally new code with no dependency on QcMarkupModal / TakeoffEditor /
 * ConceptDrawingModal — those three keep their own inlined copies of this pattern for now
 * (backlog: retrofit them onto this hook — see docs/SUBMITTAL_REVIEW_PLAN.md Phase 6).
 */
import { useState, useRef, useCallback, useEffect } from 'react'
import { loadPdfjs } from '../../lib/pdfjs.js'
import { screenToFracPure, fracToScreenPure } from './pdfPaneMath.js'

// Re-exported for convenience so a consumer can `import { screenToFracPure } from
// './useRasterViewport.js'` without knowing about the pure-math split — see pdfPaneMath.js
// for why the actual implementation lives there (no React import, so it's node-testable).
export { screenToFracPure, fracToScreenPure }

const TARGET_PX = 2800        // offscreen base-raster long-edge target (matches QcMarkupModal)
// Hard ceiling for any offscreen raster canvas — browsers silently leave an over-large canvas
// BLANK (no error) instead of throwing, so the hi-res resharpen pass (which requests
// baseScale × zoom, unbounded) is clamped here rather than risking a blank view at high zoom.
const MAX_RASTER_DIM = 8192
const MAX_RASTER_AREA = 40 * 1024 * 1024
const RESHARPEN_MS = 220       // settle delay before re-rastering at the current zoom (QC's proven value)
const THUMB_TARGET_W = 160     // thumbnail-rail render width (matches PdfPagePickerModal)
const MIN_Z = 0.15
const MAX_Z = 8

function clamp(v, lo, hi) { return Math.max(lo, Math.min(hi, v)) }

/**
 * Fetch a PDF's bytes for pdf.js. Tries a direct fetch first (works for public Supabase Storage
 * URLs and same-origin hosts); falls back to the authed `/api/pdf-proxy` for hosts that don't
 * send CORS headers (Airtable's attachment CDN — see api/pdf-proxy.js, host allow-listed).
 */
async function fetchPdfBytes(url) {
  try {
    const resp = await fetch(url)
    if (resp.ok) return await resp.arrayBuffer()
  } catch { /* CORS / network failure — fall through to the proxy */ }
  const token = typeof window !== 'undefined' && typeof window.__clerkGetToken === 'function'
    ? await window.__clerkGetToken().catch(() => null)
    : null
  const resp = await fetch('/api/pdf-proxy?url=' + encodeURIComponent(url), {
    headers: token ? { Authorization: 'Bearer ' + token } : {},
  })
  if (!resp.ok) throw new Error('Could not fetch the PDF (HTTP ' + resp.status + ').')
  return await resp.arrayBuffer()
}

/** Pure: {x,y,z} to fit the page to the container's WIDTH, anchored top-left. */
function calcFitWidth(base, cont) {
  const z = clamp((cont.clientWidth - 40) / base.canvas.width, MIN_Z, MAX_Z)
  return { x: 20, y: 20, z }
}
/** Pure: {x,y,z} to fit the WHOLE page in the container (contain-fit), centered. */
function calcFitPage(base, cont) {
  const zw = (cont.clientWidth - 40) / base.canvas.width
  const zh = (cont.clientHeight - 40) / base.canvas.height
  const z = clamp(Math.min(zw, zh), MIN_Z, MAX_Z)
  return { x: (cont.clientWidth - base.canvas.width * z) / 2, y: (cont.clientHeight - base.canvas.height * z) / 2, z }
}

/**
 * @param {{ pdfUrl?: string, pdfDoc?: object, fitMode?: 'width'|'page' }} opts
 *   pdfUrl — a URL this hook loads lazily via lib/pdfjs.js (the common case).
 *   pdfDoc — an already-loaded pdf.js document object; when present, skips the fetch/load step
 *            (for a consumer that already has the doc, e.g. shares one document across panes).
 *   fitMode — which fit the auto-fit-on-new-page behavior uses (default 'width', unchanged from
 *             before this option existed — every existing caller omits it). Read via a ref inside
 *             the page-load effect so changing it doesn't itself trigger a page/raster reload —
 *             it only takes effect on the NEXT page navigation (goToPage/next/prevPage), which is
 *             exactly what "respect my last fit choice when I turn the page" means
 *             (DocumentMarkupPane.jsx, Matt 2026-09-09). The explicit fitToWidth()/fitToPage()
 *             calls below are unaffected — they act on the current page immediately regardless.
 */
export function useRasterViewport({ pdfUrl, pdfDoc, fitMode = 'width' } = {}) {
  const fitModeRef = useRef(fitMode)
  fitModeRef.current = fitMode
  const [vp, setVp] = useState({ x: 0, y: 0, z: 1 })
  const [pageNum, setPageNum] = useState(1)
  const [numPages, setNumPages] = useState(0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [docTick, setDocTick] = useState(0)     // bumped when a new page raster is ready
  const [thumbnails, setThumbnails] = useState([])   // [{ pageNum, dataUrl }], grows incrementally

  const containerRef = useRef(null)   // wraps the canvas — measured for size + pointer coords
  const canvasRef = useRef(null)      // the single composite <canvas>
  const pdfDocRef = useRef(null)
  const pageRef = useRef(null)
  const baseScaleRef = useRef(1)
  const rasterBaseRef = useRef(null)  // { canvas, viewport }
  const rasterHiRef = useRef(null)    // { canvas, z }
  const renderTaskRef = useRef(null)
  const rafRef = useRef(null)
  const resharpenTimerRef = useRef(null)
  const pannedRef = useRef(false)     // has this page already been fit/centered once
  const vpRef = useRef(vp)
  vpRef.current = vp
  const thumbsBusyRef = useRef(false)
  const thumbPagesRef = useRef(new Set())
  const panStateRef = useRef(null)

  // ── Load the PDF document ───────────────────────────────────────────────────
  useEffect(() => {
    if (pdfDoc) {
      pdfDocRef.current = pdfDoc
      setNumPages(pdfDoc.numPages || 0)
      setPageNum(1)
      pannedRef.current = false
      thumbPagesRef.current = new Set()
      setThumbnails([])
      setError(null)
      setDocTick(t => t + 1)
      return
    }
    if (!pdfUrl) { pdfDocRef.current = null; setNumPages(0); return }
    let cancelled = false
    setLoading(true); setError(null)
    ;(async () => {
      try {
        const pdfjs = await loadPdfjs()
        if (!pdfjs) throw new Error('PDF viewer failed to load. Check your connection and try again.')
        const buf = await fetchPdfBytes(pdfUrl)
        if (cancelled) return
        const doc = await pdfjs.getDocument({ data: buf }).promise
        if (cancelled) return
        pdfDocRef.current = doc
        setNumPages(doc.numPages)
        setPageNum(1)
        pannedRef.current = false
        thumbPagesRef.current = new Set()
        setThumbnails([])
        setDocTick(t => t + 1)
      } catch (e) {
        if (!cancelled) setError(e.message || 'Could not open the PDF.')
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [pdfUrl, pdfDoc])

  // ── Raster helper — render the current page to an offscreen canvas at `scale` ──
  const renderRaster = useCallback(async (scale) => {
    const page = pageRef.current
    if (!page) return null
    const vp1 = page.getViewport({ scale: 1 })
    const longEdge = Math.max(vp1.width, vp1.height)
    const area = vp1.width * vp1.height
    // Clamp so the offscreen canvas never exceeds the browser's max canvas dimension/area.
    const safeScale = Math.max(0.1, Math.min(scale, MAX_RASTER_DIM / longEdge, Math.sqrt(MAX_RASTER_AREA / area)))
    const viewport = page.getViewport({ scale: safeScale })
    const c = document.createElement('canvas')
    c.width = Math.ceil(viewport.width); c.height = Math.ceil(viewport.height)
    if (renderTaskRef.current) { try { renderTaskRef.current.cancel() } catch { /* ignore */ } }
    const task = page.render({ canvasContext: c.getContext('2d'), viewport })
    renderTaskRef.current = task
    try { await task.promise } catch (e) { if (e?.name === 'RenderingCancelledException') return null; throw e }
    return { canvas: c, viewport }
  }, [])

  // ── Composite render ─────────────────────────────────────────────────────────
  const renderAll = useCallback(() => {
    const comp = canvasRef.current, cont = containerRef.current, base = rasterBaseRef.current
    if (!comp || !cont || !base) return
    const dpr = window.devicePixelRatio || 1
    const cw = cont.clientWidth, ch = cont.clientHeight
    if (comp.width !== Math.floor(cw * dpr) || comp.height !== Math.floor(ch * dpr)) {
      comp.width = Math.floor(cw * dpr); comp.height = Math.floor(ch * dpr)
      comp.style.width = cw + 'px'; comp.style.height = ch + 'px'
    }
    const g = comp.getContext('2d')
    g.setTransform(dpr, 0, 0, dpr, 0, 0)
    g.clearRect(0, 0, cw, ch)
    const v = vpRef.current
    const baseW = base.canvas.width, baseH = base.canvas.height
    g.save(); g.translate(v.x, v.y); g.scale(v.z, v.z)
    const hi = rasterHiRef.current
    if (hi && Math.abs(hi.z - v.z) < 0.02) g.drawImage(hi.canvas, 0, 0, baseW, baseH)
    else g.drawImage(base.canvas, 0, 0, baseW, baseH)
    g.restore()
  }, [])

  const scheduleRender = useCallback(() => {
    if (rafRef.current) return
    rafRef.current = requestAnimationFrame(() => { rafRef.current = null; renderAll() })
  }, [renderAll])

  const resharpen = useCallback(async () => {
    if (!pageRef.current) return
    const z = vpRef.current.z
    const hi = await renderRaster(baseScaleRef.current * z)
    if (!hi) return
    rasterHiRef.current = { canvas: hi.canvas, z }
    scheduleRender()
  }, [renderRaster, scheduleRender])

  const scheduleResharpen = useCallback(() => {
    if (resharpenTimerRef.current) clearTimeout(resharpenTimerRef.current)
    resharpenTimerRef.current = setTimeout(resharpen, RESHARPEN_MS)
  }, [resharpen])

  // ── Load page + base raster whenever the page or the doc changes ────────────
  useEffect(() => {
    const doc = pdfDocRef.current
    if (!doc) return
    let cancelled = false
    ;(async () => {
      try {
        const page = await doc.getPage(pageNum)
        if (cancelled) return
        pageRef.current = page
        const vp1 = page.getViewport({ scale: 1 })
        const bs = clamp(TARGET_PX / Math.max(vp1.width, vp1.height), 0.5, 4)
        baseScaleRef.current = bs
        rasterHiRef.current = null
        const base = await renderRaster(bs)
        if (cancelled || !base) return
        rasterBaseRef.current = base
        const cont = containerRef.current
        if (cont && !pannedRef.current) {
          // Auto-fit on a fresh page uses whichever mode was last explicitly chosen
          // (fitModeRef, read fresh here — not a dependency, see this hook's header comment).
          // Default 'width' matches every existing caller's original behavior exactly: a
          // previous version used Math.min(widthFit, heightFit) — a "contain" fit — which left
          // the page rendered well short of the pane's actual width whenever the pane was shorter
          // than it was wide (every side pane here, with a list above eating vertical space).
          setVp(fitModeRef.current === 'page' ? calcFitPage(base, cont) : calcFitWidth(base, cont))
          pannedRef.current = true
        }
        scheduleRender()
      } catch (e) {
        if (!cancelled) setError(e.message || 'Could not render the page.')
      }
    })()
    return () => { cancelled = true }
  }, [pageNum, docTick]) // eslint-disable-line react-hooks/exhaustive-deps

  // Redraw whenever the viewport transform changes.
  useEffect(() => { scheduleRender() }, [vp, scheduleRender])

  // Redraw when the container is resized (e.g. a collapsible sibling pane toggles, or the
  // ResizeHandle divider is dragged) — renderAll already re-measures clientWidth/Height.
  useEffect(() => {
    const cont = containerRef.current
    if (!cont || typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(() => scheduleRender())
    ro.observe(cont)
    return () => ro.disconnect()
  }, [scheduleRender])

  // ── Non-passive wheel-to-zoom (JSX onWheel can't preventDefault on a passive listener) ──
  useEffect(() => {
    const cont = containerRef.current
    if (!cont) return
    function onWheel(e) {
      if (!rasterBaseRef.current) return
      e.preventDefault()
      const v = vpRef.current
      const ns = clamp(+(v.z * (e.deltaY < 0 ? 1.12 : 1 / 1.12)).toFixed(3), MIN_Z, MAX_Z)
      if (ns === v.z) return
      const r = cont.getBoundingClientRect()
      const lx = e.clientX - r.left, ly = e.clientY - r.top
      const f = 1 - ns / v.z   // anchor zoom on the cursor
      rasterHiRef.current = null
      setVp({ x: v.x + (lx - v.x) * f, y: v.y + (ly - v.y) * f, z: ns })
      scheduleResharpen()
    }
    cont.addEventListener('wheel', onWheel, { passive: false })
    return () => cont.removeEventListener('wheel', onWheel)
  }, [scheduleResharpen])

  useEffect(() => () => {
    if (renderTaskRef.current) { try { renderTaskRef.current.cancel() } catch { /* ignore */ } }
    if (rafRef.current) cancelAnimationFrame(rafRef.current)
    if (resharpenTimerRef.current) clearTimeout(resharpenTimerRef.current)
  }, [])

  // ── Pan (plain drag) ──────────────────────────────────────────────────────────
  const beginPan = useCallback((clientX, clientY) => {
    panStateRef.current = { sx: clientX, sy: clientY, vp0: { ...vpRef.current } }
  }, [])
  const movePan = useCallback((clientX, clientY) => {
    const p = panStateRef.current
    if (!p) return
    setVp(v => ({ ...v, x: p.vp0.x + (clientX - p.sx), y: p.vp0.y + (clientY - p.sy) }))
  }, [])
  const endPan = useCallback(() => { panStateRef.current = null }, [])

  // ── Zoom buttons / fit-to-width ────────────────────────────────────────────────
  const zoomBy = useCallback((factor) => {
    const v = vpRef.current
    const ns = clamp(+(v.z * factor).toFixed(3), MIN_Z, MAX_Z)
    const cont = containerRef.current
    const lx = cont ? cont.clientWidth / 2 : 0, ly = cont ? cont.clientHeight / 2 : 0
    const f = 1 - ns / v.z
    rasterHiRef.current = null
    setVp({ x: v.x + (lx - v.x) * f, y: v.y + (ly - v.y) * f, z: ns })
    scheduleResharpen()
  }, [scheduleResharpen])
  const zoomIn = useCallback(() => zoomBy(1.2), [zoomBy])
  const zoomOut = useCallback(() => zoomBy(1 / 1.2), [zoomBy])
  const fitToWidth = useCallback(() => {
    const base = rasterBaseRef.current, cont = containerRef.current
    if (!base || !cont) return
    rasterHiRef.current = null
    setVp(calcFitWidth(base, cont))
    scheduleResharpen()
  }, [scheduleResharpen])
  // Contain-fit — the whole page visible at once, centered. A sibling to fitToWidth, not a
  // replacement: fitToWidth stays the DEFAULT auto-fit on page load (see this file's header
  // comment on why a "contain" default was wrong for a narrow side pane, and `fitMode` above for
  // how a consumer opts a whole session into contain-fit-by-default instead); this is only
  // invoked explicitly otherwise, e.g. a "Fit page" toolbar button on a full-screen consumer like
  // DocumentMarkupPane.jsx where whole-page overview is a genuinely useful mode of its own.
  const fitToPage = useCallback(() => {
    const base = rasterBaseRef.current, cont = containerRef.current
    if (!base || !cont) return
    rasterHiRef.current = null
    setVp(calcFitPage(base, cont))
    scheduleResharpen()
  }, [scheduleResharpen])

  // ── Page navigation ────────────────────────────────────────────────────────────
  const goToPage = useCallback((n) => {
    const doc = pdfDocRef.current
    if (!doc) return
    const clamped = clamp(Math.round(n), 1, doc.numPages || 1)
    pannedRef.current = false   // re-fit/center for the new page (pages may differ in size)
    setPageNum(clamped)
  }, [])
  const nextPage = useCallback(() => { setPageNum(p => { const doc = pdfDocRef.current; const max = doc?.numPages || 1; if (p >= max) return p; pannedRef.current = false; return p + 1 }) }, [])
  const prevPage = useCallback(() => { setPageNum(p => { if (p <= 1) return p; pannedRef.current = false; return p - 1 }) }, [])

  // ── Coordinate transforms — screen px (relative to containerRef) <-> page fraction [fx,fy] ──
  function baseDims() {
    const b = rasterBaseRef.current
    return b ? { bw: b.canvas.width, bh: b.canvas.height } : null
  }
  function localFromEvent(evtOrPoint) {
    if (Array.isArray(evtOrPoint)) return evtOrPoint
    if (evtOrPoint && typeof evtOrPoint.clientX === 'number') {
      const r = containerRef.current ? containerRef.current.getBoundingClientRect() : { left: 0, top: 0 }
      return [evtOrPoint.clientX - r.left, evtOrPoint.clientY - r.top]
    }
    return [evtOrPoint?.x || 0, evtOrPoint?.y || 0]
  }

  /** Screen px (relative to the container) OR a pointer/mouse event -> page fraction [fx, fy]. */
  const screenToFrac = useCallback((evtOrPoint) => {
    const [sx, sy] = localFromEvent(evtOrPoint)
    return screenToFracPure(vpRef.current, baseDims(), sx, sy)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  /** Page fraction [fx, fy] (or fx, fy as two args) -> screen px relative to the container. */
  const fracToScreen = useCallback((fx, fy) => {
    const [FX, FY] = Array.isArray(fx) ? fx : [fx, fy]
    return fracToScreenPure(vpRef.current, baseDims(), FX, FY)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Lazy thumbnail rail ────────────────────────────────────────────────────────
  // Renders pages incrementally (one at a time, smallest first) rather than all upfront — a
  // large package shouldn't block opening the rail. Call from the consumer when the rail opens.
  const ensureThumbnails = useCallback(async () => {
    const doc = pdfDocRef.current
    if (!doc || thumbsBusyRef.current) return
    thumbsBusyRef.current = true
    const myDoc = doc
    try {
      for (let n = 1; n <= (myDoc.numPages || 0); n++) {
        if (pdfDocRef.current !== myDoc) return   // doc swapped mid-flight — abandon
        if (thumbPagesRef.current.has(n)) continue
        thumbPagesRef.current.add(n)
        try {
          const page = await myDoc.getPage(n)
          const vp1 = page.getViewport({ scale: 1 })
          const scale = THUMB_TARGET_W / vp1.width
          const viewport = page.getViewport({ scale })
          const c = document.createElement('canvas')
          c.width = Math.ceil(viewport.width); c.height = Math.ceil(viewport.height)
          await page.render({ canvasContext: c.getContext('2d'), viewport }).promise
          const dataUrl = c.toDataURL('image/jpeg', 0.75)
          if (pdfDocRef.current !== myDoc) return
          setThumbnails(prev => prev.some(t => t.pageNum === n) ? prev : [...prev, { pageNum: n, dataUrl }].sort((a, b) => a.pageNum - b.pageNum))
        } catch { /* skip this page's thumbnail on failure, keep going */ }
      }
    } finally { thumbsBusyRef.current = false }
  }, [])

  return {
    // DOM refs to attach
    containerRef, canvasRef,
    // status
    loading, error,
    // pan/zoom
    vp, zoomIn, zoomOut, fitToWidth, fitToPage, beginPan, movePan, endPan,
    // pages
    pageNum, numPages, goToPage, nextPage, prevPage,
    // coordinate transforms (page-relative visual fractions, not PDF user-space)
    screenToFrac, fracToScreen,
    // thumbnail rail
    thumbnails, ensureThumbnails,
    // manual redraw (rarely needed — most state changes already schedule one)
    scheduleRender,
  }
}
